/** @format */

import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { getMaxDate } from '../../../../Services/service.home';
import { useAppContext, FILTER_NAMES } from '../../../../store/store.app';
import { isCYHotelCode } from '../../../../Components/Library/appfilter.lib';

let cachedPromise = null;
let cachedDates = null;

function parseMaxDateResponse(res) {
  if (!res?.data) {
    return {
      max_date: null,
      max_date_cy: null,
      expense_max_date: null,
      expense_max_date_cy: null,
    };
  }
  const data = res.data;

  if (typeof data === 'string') {
    return {
      max_date: data,
      max_date_cy: null,
      expense_max_date: null,
      expense_max_date_cy: null,
    };
  }

  return {
    max_date: data.max_date ?? data.date ?? data.maxDate ?? null,
    max_date_cy: data.max_date_cy ?? null,
    expense_max_date: data.expense_max_date ?? null,
    expense_max_date_cy: data.expense_max_date_cy ?? null,
  };
}

function setMaxDateIfChanged(setter, seed) {
  setter((prev) => {
    const next = seed ? dayjs(seed) : null;

    if (prev && next && prev.valueOf() === next.valueOf()) {
      return prev;
    }

    if (!prev && !next) {
      return prev;
    }

    return next;
  });
}

function isCYHotel(appFilters) {
  const params = appFilters[FILTER_NAMES.PARAMETER];
  if (!Array.isArray(params)) return false;
  const hotelParam = params.find((p) => p.name === 'hotel');
  const selected = hotelParam?.selected || [];
  return selected.some(isCYHotelCode);
}

export default function useMaxDate(mode = 'revenue') {
  const isExpense = mode === 'expense';

  const {
    appStore: { appFilters },
  } = useAppContext();

  const isCY = isCYHotel(appFilters);

  const [maxDate, setMaxDate] = useState(() => {
    if (!cachedDates) return null;
    const seed = isExpense
      ? isCY
        ? cachedDates.expense_max_date_cy
        : cachedDates.expense_max_date
      : isCY
        ? cachedDates.max_date_cy
        : cachedDates.max_date;
    return seed ? dayjs(seed) : null;
  });

  useEffect(() => {
    let mounted = true;

    if (cachedDates) {
      const seed = isExpense
        ? isCY
          ? cachedDates.expense_max_date_cy
          : cachedDates.expense_max_date
        : isCY
          ? cachedDates.max_date_cy
          : cachedDates.max_date;
      if (mounted) setMaxDateIfChanged(setMaxDate, seed);
      return;
    }

    if (!cachedPromise) {
      cachedPromise = getMaxDate()
        .then((res) => {
          cachedDates = parseMaxDateResponse(res);
          cachedPromise = null;
          return cachedDates;
        })
        .catch(() => {
          cachedPromise = null;
          cachedDates = {
            max_date: null,
            max_date_cy: null,
            expense_max_date: null,
            expense_max_date_cy: null,
          };
          return cachedDates;
        });
    }

    cachedPromise.then((dates) => {
      if (!mounted) return;
      const seed = isExpense
        ? isCY
          ? dates?.expense_max_date_cy
          : dates?.expense_max_date
        : isCY
          ? dates?.max_date_cy
          : dates?.max_date;
      setMaxDateIfChanged(setMaxDate, seed);
    });

    return () => {
      mounted = false;
    };
  }, [isExpense, isCY]);

  return maxDate;
}
